import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts';
import { toast } from 'react-hot-toast';

const ReportsContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ReportCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const ReportTitle = styled.h2`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: ${({ theme }) => theme.typography.semibold};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const ReportDetails = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.base};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const DownloadButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid ${({ theme }) => theme.colors.gray200};
    border-top-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin: ${({ theme }) => theme.spacing[4]} 0;
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing[4]};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

interface Report {
  id: string;
  name: string;
  type: string;
  date: string;
  status: string;
}


const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      // TODO: Implement real API call to fetch reports
      setReports([]);
    } catch (err) {
      setError('Failed to load reports');
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleDownload = async (reportId: string) => {
    try {
      // Mock download for now
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ReportsContainer>
        <Title>Reports</Title>
        <ErrorContainer>
          {error}
          <RetryButton onClick={fetchReports}>Retry</RetryButton>
        </ErrorContainer>
      </ReportsContainer>
    );
  }

  return (
    <ReportsContainer>
      <Title>Reports</Title>
      <ReportsGrid>
        {reports.map((report) => (
          <ReportCard key={report.id}>
            <ReportTitle>{report.name}</ReportTitle>
            <ReportDetails>
              <div>Type: {report.type}</div>
              <div>Date: {new Date(report.date).toLocaleDateString()}</div>
              <div>Status: {report.status}</div>
            </ReportDetails>
            <DownloadButton onClick={() => handleDownload(report.id)}>
              Download
            </DownloadButton>
          </ReportCard>
        ))}
      </ReportsGrid>
    </ReportsContainer>
  );
};

export default ReportsPage; 